import type { ReactNode } from "react";
import { 
  Plus, 
  Trash2, 
  Layers, 
  Search, 
  Filter, 
  Check, 
  Loader2, 
  AlertCircle, 
  Globe, 
  Users, 
  Lock, 
  ChevronRight, 
  ArrowUp, 
  ArrowDown, 
  Type, 
  Palette,
  X,
  Database,
  Info
} from "lucide-react";
import type { 
  MapSourcesPaneProps, 
  MapWorkspaceShareScope,
  MapSourceLayerGroup,
  MapSourceCatalogItem,
  MapWorkspaceAttachedSpriteItem,
  MapWorkspaceSpriteCatalogItem,
  MapWorkspaceAssetOption
} from "../../contracts/map-workspace";
import { cn } from "../../lib/utils";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../internal/primitives/tabs";
import { Input } from "../../internal/primitives/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../internal/primitives/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../internal/primitives/accordion";
import { Switch } from "../../internal/primitives/switch";
import { Label } from "../../internal/primitives/label";

const ShareScopeIcon = ({ scope }: { scope?: MapWorkspaceShareScope | null }) => {
  switch (scope) {
    case "public":
      return <Globe className="h-3 w-3" />;
    case "shared":
      return <Users className="h-3 w-3" />;
    case "tenant":
      return <Lock className="h-3 w-3" />;
    default:
      return null;
  }
}

export function MapSourcesPane({
  activeTab,
  catalogSources,
  mountedGroups,
  spriteCatalog,
  attachedSprites,
  glyphOptions,
  selectedGlyphId,
  searchValue,
  scopeValue,
  scopeOptions,
  loading,
  errorMessage,
  spriteLoading,
  spriteErrorMessage,
  fontLoading,
  fontErrorMessage,
  emptyCatalogMessage = "No sources found matching your criteria.",
  emptyMountedMessage = "No sources are mounted in this workspace.",
  emptySpriteCatalogMessage = "No sprite sheets found.",
  emptyAttachedSpritesMessage = "No sprite sheets attached.",
  emptyFontsMessage = "No glyph bundles available.",
  onTabChange,
  onSearchChange,
  onScopeChange,
  onAddSource,
  onAddSprite,
  onSelectSprite,
  onRemoveSprite,
  onMoveSprite,
  onToggleLayer,
  onSelectLayer,
  onRemoveSource,
  onSelectGlyph,
  onClearGlyph,
  embedded = false,
  headerContent,
  headerAction,
  className,
}: MapSourcesPaneProps) {
  return (
    <div className={cn("flex flex-col h-full bg-background min-h-0", className)}>
      {!embedded ? (
        <header className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-3 shrink-0">
          <div className="min-w-0 flex-1">
            {headerContent ?? (
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-primary/20 bg-primary/10">
                  <Database className="h-4 w-4 text-primary" />
                </div>
                <div className="flex min-w-0 flex-col cursor-default">
                  <span className="truncate text-[11px] font-black uppercase tracking-wider text-foreground">
                    Source Registry
                  </span>
                  <span className="truncate text-[9px] font-bold uppercase tracking-tighter text-muted-foreground opacity-70">
                    Workspace Context
                  </span>
                </div>
              </div>
            )}
          </div>
          {headerAction ?? (
            <button className="rounded-full p-2 text-muted-foreground transition-colors hover:bg-muted">
              <Info className="h-4 w-4" />
            </button>
          )}
        </header>
      ) : null}
      
      <Tabs 
        value={activeTab} 
        onValueChange={(value) => onTabChange(value as "layers" | "sprites" | "fonts")} 
        className="flex-1 flex flex-col min-h-0"
      >
        <div className={cn(
          "px-4 border-b shrink-0",
          embedded ? "bg-muted/10" : "bg-muted/20"
        )}>
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 gap-6 rounded-none overflow-x-auto scrollbar-none">
            <InventoryTabTrigger
              value="layers"
              label="Layers"
              icon={<Layers className="w-3.5 h-3.5" />}
              count={mountedGroups.length}
            />
            <InventoryTabTrigger
              value="sprites"
              label="Sprites"
              icon={<Palette className="w-3.5 h-3.5" />}
              count={attachedSprites.length}
            />
            <InventoryTabTrigger
              value="fonts"
              label="Fonts"
              icon={<Type className="w-3.5 h-3.5" />}
              count={selectedGlyphId ? 1 : 0}
            />
          </TabsList>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-muted">
          <TabsContent value="layers" className="m-0 p-0 focus-visible:ring-0 space-y-6 animate-in fade-in duration-300">
            {/* Search & Filter Row */}
            <div className="p-4 bg-muted/5 border-b border-border/50">
              <SearchAndFilter
                searchValue={searchValue}
                scopeValue={scopeValue}
                scopeOptions={scopeOptions}
                onSearchChange={onSearchChange}
                onScopeChange={onScopeChange}
                placeholder="Search sources..."
              />
            </div>

            <div className="px-4 space-y-8 pb-8">
              {/* Mounted Sources Section */}
              <div className="space-y-4">
                <InventorySectionHeader
                  title="Mounted In Workspace"
                  description="Active sources and rendered layers."
                />
                {mountedGroups.length === 0 ? (
                  <EmptyState
                    icon={<Layers className="w-8 h-8" />}
                    title="No sources mounted"
                    description={emptyMountedMessage ?? "No sources are mounted."}
                  />
                ) : (
                  <Accordion type="multiple" className="space-y-2">
                    {mountedGroups.map((group) => (
                      <MountedSourceGroup
                        key={group.sourceId}
                        group={group}
                        onToggleLayer={onToggleLayer}
                        onSelectLayer={onSelectLayer}
                        onRemoveSource={onRemoveSource}
                      />
                    ))}
                  </Accordion>
                )}
              </div>

              {/* Source Catalog (Discovery) Section */}
              <div className="space-y-4">
                <InventorySectionHeader
                  title="Source Registry"
                  description="Discover and add sources to your workspace."
                />
                <SourceCatalogDiscovery
                  sources={catalogSources}
                  loading={loading}
                  errorMessage={errorMessage ?? null}
                  emptyMessage={emptyCatalogMessage ?? "No sources found."}
                  onAddSource={onAddSource}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="sprites" className="m-0 p-0 focus-visible:ring-0 space-y-6 animate-in fade-in duration-300">
            {/* Search & Filter Row */}
            <div className="p-4 bg-muted/5 border-b border-border/50">
              <SearchAndFilter
                searchValue={searchValue}
                scopeValue={scopeValue}
                scopeOptions={scopeOptions}
                onSearchChange={onSearchChange}
                onScopeChange={onScopeChange}
                placeholder="Search sprite sheets..."
              />
            </div>

            <div className="px-4 space-y-8 pb-8">
              {/* Attached Sprites Section */}
              <div className="space-y-4">
                <InventorySectionHeader
                  title="Attached Sprite Sheets"
                  description="Ordered icons and patterns available for styling."
                />
                <AttachedSpritesList
                  sprites={attachedSprites}
                  emptyMessage={emptyAttachedSpritesMessage ?? "No sprite sheets attached."}
                  onSelect={onSelectSprite}
                  onRemove={onRemoveSprite}
                  onMove={onMoveSprite}
                />
              </div>

              {/* Sprite Catalog (Discovery) Section */}
              <div className="space-y-4">
                <InventorySectionHeader
                  title="Sprite Library"
                  description="Find and attach icon sets to this workspace."
                />
                <SpriteCatalogDiscovery
                  sprites={spriteCatalog}
                  loading={spriteLoading}
                  errorMessage={spriteErrorMessage ?? null}
                  emptyMessage={emptySpriteCatalogMessage ?? "No sprite sheets found."}
                  onAddSprite={onAddSprite}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="fonts" className="m-0 p-4 focus-visible:ring-0 space-y-6 animate-in fade-in duration-300">
            <InventorySectionHeader
              title="Typography & Glyphs"
              description="Manage the active glyph bundle for text rendering."
            />
            <FontManagement
              options={glyphOptions}
              selectedId={selectedGlyphId}
              loading={fontLoading}
              errorMessage={fontErrorMessage ?? null}
              emptyMessage={emptyFontsMessage ?? "No glyph bundles available."}
              onSelect={onSelectGlyph}
              onClear={onClearGlyph}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

/**
 * Sub-components
 */

function InventoryTabTrigger({ value, label, count, icon }: { value: string, label: string, count?: number, icon: ReactNode }) {
  return (
    <TabsTrigger
      value={value}
      className="bg-transparent border-b-2 border-transparent rounded-none data-[state=active]:bg-transparent data-[state=active]:border-primary data-[state=active]:shadow-none px-0 pb-3 flex items-center gap-2 group transition-all"
    >
      <div className="text-muted-foreground group-data-[state=active]:text-primary">
        {icon}
      </div>
      <span className="text-[11px] font-bold tracking-tight uppercase group-data-[state=active]:text-foreground text-muted-foreground">
        {label}
      </span>
      {typeof count === "number" && count > 0 ? (
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground group-data-[state=active]:bg-primary/10 group-data-[state=active]:text-primary transition-colors border border-border/30">
          {count}
        </span>
      ) : null}
    </TabsTrigger>
  )
}

function InventorySectionHeader({ title, description, onAdd, addLabel }: { title: string, description: string, onAdd?: () => void, addLabel?: string }) {
  return (
    <div className="flex items-center justify-between px-1">
      <div className="flex flex-col gap-1">
        <Label className="text-[10px] font-black uppercase text-muted-foreground/80 tracking-widest leading-none">{title}</Label>
        <p className="text-[10px] text-muted-foreground italic opacity-80">{description}</p>
      </div>
      {onAdd && addLabel && (
        <button
          type="button"
          onClick={onAdd}
          className="text-[10px] font-bold text-primary flex items-center gap-1 hover:bg-primary/5 px-2 py-1 rounded border border-primary/10 transition-colors"
        >
          <Plus className="w-3 h-3" /> {addLabel}
        </button>
      )}
    </div>
  )
}

function SearchAndFilter({ 
  searchValue, 
  scopeValue, 
  scopeOptions, 
  onSearchChange, 
  onScopeChange,
  placeholder 
}: { 
  searchValue: string, 
  scopeValue: "all" | MapWorkspaceShareScope, 
  scopeOptions: Array<{ value: "all" | MapWorkspaceShareScope; label: string }>, 
  onSearchChange: (v: string) => void, 
  onScopeChange: (v: "all" | MapWorkspaceShareScope) => void,
  placeholder: string
}) {
  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1">
        <Search className="absolute left-2.5 top-2 h-3 w-3 text-muted-foreground/60" />
        <Input
          placeholder={placeholder}
          className="h-8 bg-background pl-8 pr-8 text-[11px] font-medium border-border/60 focus:border-primary/50"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        {searchValue ? (
          <button
            onClick={() => onSearchChange("")}
            className="absolute right-2 top-2 text-muted-foreground/40 hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        ) : null}
      </div>
      <Select
        value={scopeValue}
        onValueChange={onScopeChange}
      >
        <SelectTrigger className="h-8 w-[110px] bg-background text-[10px] font-bold uppercase tracking-tight border-border/60">
          <div className="flex items-center gap-1.5 truncate">
            <Filter className="h-2.5 w-2.5 text-muted-foreground/60" />
            <SelectValue placeholder="Scope" />
          </div>
        </SelectTrigger>
        <SelectContent>
          {scopeOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} className="text-[10px] font-bold uppercase">
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function MountedSourceGroup({ 
  group, 
  onToggleLayer, 
  onSelectLayer, 
  onRemoveSource 
}: { 
  group: MapSourceLayerGroup,
  onToggleLayer: (sid: string, lid: string, r: boolean) => void,
  onSelectLayer?: (sid: string, lid: string) => void,
  onRemoveSource?: (sid: string) => void
}) {
  return (
    <AccordionItem
      value={group.sourceId}
      className="overflow-hidden rounded-lg border border-border/60 bg-muted/5 shadow-sm"
    >
      <AccordionTrigger className="group/trigger px-3 py-3 transition-colors hover:bg-muted/30 hover:no-underline">
        <div className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <div className="flex min-w-0 flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <span className="truncate text-[11px] font-bold leading-none text-foreground">{group.label}</span>
              <span className="rounded border border-border/30 bg-muted/80 px-1 py-0.5 text-[8px] font-mono text-muted-foreground">
                {group.alias}
              </span>
            </div>
            <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-tight text-muted-foreground/60">
              <div className="flex items-center gap-1">
                <ShareScopeIcon scope={group.shareScope} />
                {group.shareScope}
              </div>
              <span className="opacity-30">•</span>
              <span>{group.renderedCount}/{group.layers.length} Layers</span>
            </div>
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="border-t border-border/20 bg-background/50 p-0">
        {onRemoveSource && (
          <div className="flex items-center justify-between border-b border-border/10 bg-muted/20 px-3 py-2">
            <div className="flex items-center gap-1.5 text-[8px] font-black uppercase tracking-widest text-muted-foreground/40">
              <Info className="h-2.5 w-2.5" />
              Source Management
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveSource(group.sourceId);
              }}
              className="inline-flex items-center gap-1 rounded-sm border border-border/60 bg-background px-2 py-1 text-[8px] font-black uppercase tracking-widest text-muted-foreground transition-all hover:border-destructive/40 hover:bg-destructive/10 hover:text-destructive active:scale-95"
            >
              <Trash2 className="h-2.5 w-2.5" />
              Unmount
            </button>
          </div>
        )}

        <div className="flex flex-col">
          {group.layers.map((layer) => (
            <div
              key={layer.id}
              className={cn(
                "grid grid-cols-[1fr_auto] items-center gap-2 border-b border-border/5 px-3 py-2.5 transition-all last:border-0",
                layer.current ? "bg-primary/[0.04] shadow-[inset_2px_0_0_0_theme(colors.primary.DEFAULT)]" : "hover:bg-muted/20"
              )}
            >
              <button
                type="button"
                onClick={() => onSelectLayer?.(group.sourceId, layer.id)}
                disabled={!layer.rendered || !onSelectLayer}
                className="flex min-w-0 flex-1 flex-col gap-1 text-left disabled:cursor-default"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className={cn(
                      "truncate text-[11px] font-bold leading-tight transition-colors",
                      layer.current ? "text-primary" : "text-foreground",
                      !layer.rendered && "text-muted-foreground/50"
                    )}
                  >
                    {layer.label}
                  </span>
                  {layer.current ? <Check className="h-3 w-3 shrink-0 text-primary" /> : null}
                </div>
                <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-tight text-muted-foreground/50">
                  {layer.geometryLabel ? <span className="truncate">{layer.geometryLabel}</span> : null}
                  {layer.styleLayerCount !== null && layer.styleLayerCount !== undefined ? (
                    <span className="whitespace-nowrap opacity-60">{layer.styleLayerCount} Rules</span>
                  ) : null}
                </div>
              </button>
              <div className="flex items-center">
                <Switch
                  checked={layer.rendered}
                  onCheckedChange={(checked) => onToggleLayer(group.sourceId, layer.id, checked)}
                  className="scale-75 data-[state=checked]:bg-primary"
                />
              </div>
            </div>
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  )
}

function SourceCatalogDiscovery({ 
  sources, 
  loading, 
  errorMessage, 
  emptyMessage, 
  onAddSource 
}: { 
  sources: MapSourceCatalogItem[],
  loading?: boolean,
  errorMessage?: string | null,
  emptyMessage: string,
  onAddSource: (id: string) => void
}) {
  if (loading) return <LoadingState message="Syncing Source Registry..." />;
  if (errorMessage) return <ErrorState message={errorMessage} />;
  if (sources.length === 0) return <EmptyState icon={<Database className="w-8 h-8" />} title="Registry empty" description={emptyMessage} />;

  return (
    <div className="grid gap-2">
      {sources.map((source) => (
        <DiscoveryCard
          key={source.id}
          label={source.label}
          description={source.description}
          shareScope={source.shareScope}
          alreadyAdded={source.alreadyMounted}
          onAdd={() => onAddSource(source.id)}
          addLabel="Mount"
        />
      ))}
    </div>
  )
}

function SpriteCatalogDiscovery({ 
  sprites, 
  loading, 
  errorMessage, 
  emptyMessage, 
  onAddSprite 
}: { 
  sprites: MapWorkspaceSpriteCatalogItem[],
  loading?: boolean,
  errorMessage?: string | null,
  emptyMessage: string,
  onAddSprite: (id: string) => void
}) {
  if (loading) return <LoadingState message="Scanning Sprite Assets..." />;
  if (errorMessage) return <ErrorState message={errorMessage} />;
  if (sprites.length === 0) return <EmptyState icon={<Palette className="w-8 h-8" />} title="Library empty" description={emptyMessage} />;

  return (
    <div className="grid gap-2">
      {sprites.map((sprite) => (
        <DiscoveryCard
          key={sprite.id}
          label={sprite.label}
          description={sprite.description}
          shareScope={sprite.shareScope}
          alreadyAdded={sprite.alreadyAttached}
          onAdd={() => onAddSprite(sprite.id)}
          addLabel="Attach"
        />
      ))}
    </div>
  )
}

function AttachedSpritesList({ 
  sprites, 
  emptyMessage, 
  onSelect, 
  onRemove, 
  onMove 
}: { 
  sprites: MapWorkspaceAttachedSpriteItem[],
  emptyMessage: string,
  onSelect?: (id: string) => void,
  onRemove?: (id: string) => void,
  onMove?: (id: string, dir: "up" | "down") => void
}) {
  if (sprites.length === 0) return <EmptyState icon={<Palette className="w-8 h-8" />} title="No sprites" description={emptyMessage} />;

  return (
    <div className="grid gap-2">
      {sprites.map((sprite) => (
        <div
          key={sprite.id}
          onClick={() => onSelect?.(sprite.id)}
          className={cn(
            "group p-2.5 border rounded-lg bg-card flex items-center gap-3 transition-all cursor-pointer shadow-sm",
            sprite.active ? "border-primary/30 bg-primary/5" : "hover:border-primary/20"
          )}
        >
          <div className="h-8 w-8 rounded border-2 border-background bg-muted/20 flex items-center justify-center shrink-0">
            <Palette className="w-4 h-4 text-muted-foreground/40" />
          </div>

          <div className="flex flex-1 flex-col min-w-0">
            <div className="flex items-center gap-2">
              <span className={cn(
                "text-[11px] font-bold truncate",
                sprite.active ? "text-primary" : "text-foreground"
              )}>
                {sprite.label}
              </span>
              <span className="text-[8px] text-muted-foreground uppercase font-bold tracking-tighter bg-muted px-1 rounded">
                Order {sprite.order}
              </span>
            </div>
            {sprite.description && (
              <div className="mt-0.5 line-clamp-1 text-[10px] text-muted-foreground/80">
                {sprite.description}
              </div>
            )}
          </div>

          <div className="flex items-center gap-0.5 shrink-0 px-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {onMove && (
              <>
                <button
                  type="button"
                  disabled={!sprite.canMoveUp}
                  onClick={(e) => { e.stopPropagation(); onMove(sprite.id, "up"); }}
                  className="p-1 rounded text-muted-foreground/40 hover:text-primary hover:bg-primary/10 disabled:opacity-20"
                >
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  disabled={!sprite.canMoveDown}
                  onClick={(e) => { e.stopPropagation(); onMove(sprite.id, "down"); }}
                  className="p-1 rounded text-muted-foreground/40 hover:text-primary hover:bg-primary/10 disabled:opacity-20"
                >
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </>
            )}
            {onRemove && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRemove?.(sprite.id); }}
                className="p-1 rounded text-muted-foreground/40 hover:text-destructive hover:bg-destructive/10"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            <ChevronRight className={cn(
              "w-4 h-4 ml-1",
              sprite.active ? "text-primary" : "text-muted-foreground/20"
            )} />
          </div>
        </div>
      ))}
    </div>
  )
}

function FontManagement({ 
  options, 
  selectedId, 
  loading, 
  errorMessage, 
  emptyMessage, 
  onSelect, 
  onClear 
}: { 
  options: MapWorkspaceAssetOption[],
  selectedId?: string | null,
  loading?: boolean,
  errorMessage?: string | null,
  emptyMessage: string,
  onSelect?: (id: string) => void,
  onClear?: () => void
}) {
  if (loading) return <LoadingState message="Loading Font Options..." />;
  if (errorMessage) return <ErrorState message={errorMessage} />;
  if (options.length === 0) return <EmptyState icon={<Type className="w-8 h-8" />} title="No fonts found" description={emptyMessage} />;

  return (
    <div className="grid gap-2">
      {options.map((option) => (
        <div
          key={option.id}
          onClick={() => onSelect?.(option.id)}
          className={cn(
            "group p-3 border rounded-lg bg-card flex items-center justify-between gap-4 transition-all cursor-pointer shadow-sm",
            selectedId === option.id ? "border-primary/30 bg-primary/5" : "hover:border-primary/20"
          )}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded bg-muted/20 flex items-center justify-center shrink-0">
              <Type className="w-4 h-4 text-muted-foreground/40" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className={cn(
                "text-[11px] font-bold truncate",
                selectedId === option.id ? "text-primary" : "text-foreground"
              )}>
                {option.label}
              </span>
              {option.description && (
                <span className="text-[10px] text-muted-foreground/80 line-clamp-1">{option.description}</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {selectedId === option.id ? (
              <div className="flex items-center gap-2">
                <span className="text-[9px] font-bold uppercase text-primary tracking-wider">Active Bundle</span>
                {onClear && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onClear(); }}
                    className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground/40 hover:text-destructive transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ) : (
              <Plus className="w-4 h-4 text-muted-foreground/20 group-hover:text-primary/50 transition-colors" />
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

function DiscoveryCard({ 
  label, 
  description, 
  shareScope, 
  alreadyAdded, 
  onAdd, 
  addLabel 
}: { 
  label: string, 
  description?: string | null, 
  shareScope?: MapWorkspaceShareScope | null, 
  alreadyAdded?: boolean, 
  onAdd: () => void,
  addLabel: string
}) {
  return (
    <div
      className={cn(
        "group relative flex items-start justify-between gap-4 rounded-lg border border-border/50 bg-muted/5 p-3 transition-all",
        alreadyAdded ? "bg-muted/10 opacity-60" : "hover:border-primary/30 hover:bg-primary/[0.02]"
      )}
    >
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[11px] font-bold leading-tight text-foreground/90">
            {label}
          </span>
          <div className="flex items-center gap-1 rounded border border-border/30 bg-muted px-1 py-0.5 text-[8px] font-bold uppercase tracking-tighter text-muted-foreground/70">
            <ShareScopeIcon scope={shareScope} />
            {shareScope}
          </div>
        </div>
        {description && (
          <p className="line-clamp-2 text-[10px] font-medium leading-normal text-muted-foreground/60">
            {description}
          </p>
        )}
      </div>
      <button
        onClick={() => !alreadyAdded && onAdd()}
        disabled={alreadyAdded}
        className={cn(
          "flex h-7 shrink-0 items-center justify-center rounded-md border px-3 text-[9px] font-bold uppercase tracking-widest transition-all",
          alreadyAdded
            ? "cursor-not-allowed border-border/40 bg-muted/40 text-muted-foreground/40"
            : "border-primary bg-primary text-primary-foreground shadow-sm hover:scale-[1.02] hover:shadow-primary/20 active:scale-95"
        )}
      >
        {alreadyAdded ? (
          <Check className="h-3.5 w-3.5" />
        ) : (
          <>
            <Plus className="mr-1 h-3 w-3 stroke-[2.5]" />
            {addLabel}
          </>
        )}
      </button>
    </div>
  )
}

function LoadingState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground/40">
      <Loader2 className="mb-4 h-6 w-6 animate-spin opacity-30 text-primary" />
      <p className="animate-pulse text-[9px] font-black uppercase tracking-widest">{message}</p>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-destructive/20 bg-destructive/5 px-6 py-8 text-center text-destructive/80">
      <AlertCircle className="mb-3 h-5 w-5 opacity-40" />
      <p className="text-[10px] font-bold leading-snug">{message}</p>
    </div>
  )
}

function EmptyState({ icon, title, description }: { icon: ReactNode, title: string, description: string }) {
  return (
    <div className="border border-dashed rounded-lg bg-muted/5 flex flex-col items-center justify-center text-center p-8">
      <div className="text-muted-foreground/30 mb-3">{icon}</div>
      <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-tight">{title}</p>
      <p className="text-[10px] text-muted-foreground/60 mt-1 max-w-[200px]">{description}</p>
    </div>
  )
}
